import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode
} from "react";

import { clearToken, getToken, setToken } from "../api/client.js";

export interface Operator {
  id: string;
  email: string;
  name: string;
}

export interface AuthState {
  token: string | null;
  operator: Operator | null;
  login: (token: string) => void;
  logout: () => void;
}

// Decodes the JWT payload only — signature verification is the server's job.
function decodeOperator(token: string): Operator | null {
  try {
    const payloadSegment = token.split(".")[1];
    if (!payloadSegment) {
      return null;
    }
    const json = atob(payloadSegment.replace(/-/g, "+").replace(/_/g, "/"));
    const claims = JSON.parse(json) as {
      sub?: string;
      email?: string;
      name?: string;
    };
    if (!claims.sub || !claims.email || !claims.name) {
      return null;
    }
    return { id: claims.sub, email: claims.email, name: claims.name };
  } catch {
    return null;
  }
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [token, setTokenState] = useState<string | null>(() => getToken());

  const value = useMemo<AuthState>(() => {
    return {
      token,
      operator: token ? decodeOperator(token) : null,
      login: (newToken: string) => {
        setToken(newToken);
        setTokenState(newToken);
      },
      logout: () => {
        clearToken();
        setTokenState(null);
      }
    };
  }, [token]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an <AuthProvider>");
  }
  return ctx;
}
